import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toastr } from "react-redux-toastr";
import { DartsGamesState, DartsGameData, GetDartsGamesResponse } from "./DartsGamesCRUD.types";
import { ICreateDartsGamePayload, IDartsGamePlayer, IDartsGamesFilter, IDartsTournament, IEditDartsGamePayload, IGamesInitData } from "./DartsGamesCRUD.interface";
import { api } from "../../../services/api/api.service";
import { API_DARTS_GAMES, API_DARTS_GAMES_INIT_DATA } from "../../../services/api/api.urls";
import { IDartsGroup } from "../../Tournaments/TournamentsCRUD.types";
import { createFilterParamsPayload } from "./helpers/createFilterParamsPayload";
import { IdName } from "../../../common/types/IdName";
import { COMPANY_ABBR } from "../../../common/constants/company";
import { TryCatch } from "../../../services/TryCatch";

const initialState: DartsGamesState = {
    dartsGames: [] as DartsGameData[],
    initGamesData: {
        statuses: [] as IdName[],
        tournaments: [] as IDartsTournament[],
        players: [] as IDartsGamePlayer[],
        countries: [] as IdName[],
        groups: [] as IDartsGroup[],
        competitions: [] as IDartsTournament[],
        phases: [] as IdName<IdName>[],
        types:[] as IdName[]
    },
    loadingGames: null,
    editMode: false,
    editGameId: null,
    isVisibleForm: false,
    isVisibleFullWeekForm: false,
    noContent: null,
    filterValues: {
        competitionID: 0,
        Week: 0,
        Group: 0,
        Phase: 0,
        location: null,
        Player: 0,
        From: "",
        Status: 0,
        gameDateDirection: 0,

    },
    pagination: {
        totalPages: 1,
        activePage: 1,
        pageSize: 10,
    }
}

export const getDartsGames = createAsyncThunk<GetDartsGamesResponse, string, { rejectValue: string }>(
    'dartsGames/get',
    async (queryParams, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_GAMES}?${queryParams}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch darts games.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const onClearFilterParams = createAsyncThunk<void, void, { rejectValue: string }>(
    'dartsGames/clearFilterParams',
    async (_, {dispatch, rejectWithValue}) => {
        dispatch(clearFilterParams());
        const result = await TryCatch(
            () => api.get(`${API_DARTS_GAMES}?pageNum=${1}&pageSize=${10}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to clear the filter params.");
            return rejectWithValue(result.error);
        }
        const { allGames } = result.data;
        dispatch(setDartsGames(allGames));
    }
)

export const getDartsGamesInit = createAsyncThunk<IGamesInitData | undefined, void, { rejectValue: string }>(
    'dartsGames/getDartsInit',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_GAMES_INIT_DATA}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch darts games schedule data.");
            return rejectWithValue(result.error);
        }
        return result.data as IGamesInitData;
    }
)

export const createDartsGame = createAsyncThunk<DartsGameData, ICreateDartsGamePayload, { rejectValue: string }>(
    'dartsGames/create',
    async (game, { rejectWithValue }) => {
        let status = 0;
        const result = await TryCatch(
            () => api.post(API_DARTS_GAMES, game).then(res => { status = res.status; return res.data; } )
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create game.");
            return rejectWithValue(result.error);
        }
        if (status === 200 || status === 204) {
            toastr.warning(COMPANY_ABBR, "This game is already exist.");
            //    return rejectWithValue("This game is already exist");
        }
        if (status === 201) {
            toastr.success(COMPANY_ABBR, "Darts game has been created.");
        }
        return result.data;
    }
)

export const editDartsGame = createAsyncThunk<DartsGameData, IEditDartsGamePayload, { rejectValue: string }>(
    'dartsGames/edit',
    async (game, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_DARTS_GAMES}`, game).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit game.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts game has been edited.");
        return result.data;
    }
)

//ToDo: ������ deleteDartsGame ���������� IDartsTour?
//export const deleteDartsGame = createAsyncThunk<IDartsTour, { type: number, id: number }, { rejectValue: string }>(
//    'dartsGames/delete',
//    async ({type, id}, {rejectWithValue}) => {
//        const result = await TryCatch(
//            () => client.delete(`${API_DARTS_GAMES}/${type}/${id}`).then(res => res.data)
//        );
//        if (!result.success) {
//            toastr.error(COMPANY_ABBR, "Failed to delete game.");
//            return rejectWithValue(result.error);
//        }
//        toastr.success(COMPANY_ABBR, "Darts game has been deleted.");
//        return result.data;
//    }
//);

export const DartsGamesCRUD = createSlice({name: 'DartsGamesCRUD',
    reducers: {
        showGamesForm: (state) => {
            state.isVisibleForm = true
        },
        hideGamesForm: (state,) => {
            state.isVisibleForm = false;
            state.editMode = false;
        },
        setEditMode: (state, action: PayloadAction<number>) => {
            state.editMode = true
            state.isVisibleForm = true;
            state.editGameId = action.payload;
        },
        clearFilterParams: (state) => {
            state.filterValues = createFilterParamsPayload()
        },
        setDartsGames: (state, action: PayloadAction<DartsGameData[]>) => {
            state.dartsGames = action.payload;
        },

        setGamesNoContent: (state, action: PayloadAction<boolean>) => {
            state.noContent = action.payload;
        },
        setGameFilterValues: (state, action: PayloadAction<IDartsGamesFilter>) => {
            const {field, value} = action.payload;
            state.filterValues = {...state.filterValues, [field]: value};
        },
        setActivePage: (state, action) => {
            state.pagination.activePage = action.payload
        },
        changePageSize: (state, action) => {
            state.pagination.pageSize = action.payload
        }

    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getDartsGames.pending, (state) => {
                state.loadingGames = true;
            })
            .addCase(getDartsGames.fulfilled, (state, action) => {
                if (action.payload) {
                    state.dartsGames = action.payload.items;
                    state.noContent = false;
                    state.loadingGames = false;
                    state.pagination.totalPages = Math.ceil(action.payload.totalItems / 10);
                }
            })
            .addCase(getDartsGames.rejected, (state) => {
                state.loadingGames = false;
            })
            .addCase(createDartsGame.pending, (state) => {
                state.loadingGames = true;
                state.isVisibleForm = false;
            })
            .addCase(createDartsGame.fulfilled, (state, action) => {
                state.loadingGames = false;
                state.dartsGames = state.dartsGames.concat(action.payload);
                state.editGameId = null
                state.editMode = false
            })
            .addCase(createDartsGame.rejected, (state) => {
                state.loadingGames = false;
            })
            .addCase(editDartsGame.pending, (state) => {
                state.loadingGames = true;
                state.isVisibleForm = false;
            })
            .addCase(editDartsGame.fulfilled, (state, action) => {
                state.loadingGames = false;
                state.dartsGames = state.dartsGames.map((game) => {
                    if (game.id === action.payload.id) {
                        game = action.payload
                    }
                    return game
                })
                state.editGameId = null
                state.editMode = false
            })
            .addCase(editDartsGame.rejected, (state) => {
                state.loadingGames = false;
            })

            .addCase(getDartsGamesInit.fulfilled, (state, action) => {
                state.initGamesData = action.payload as IGamesInitData;
            })
            //.addCase(deleteDartsGame.pending, (state) => {
            //    state.loadingGames = true;
            //})
            //.addCase(deleteDartsGame.fulfilled, (state, action) => {
            //    state.loadingGames = false;
            //    state.dartsGames = state.dartsGames.filter(
            //        (game) => game.id !== action.meta.arg.id
            //    );
            //})
            //.addCase(deleteDartsGame.rejected, (state) => {
            //    state.loadingGames = false;
            //})


    }
})
export const {
    showGamesForm,
    hideGamesForm,
    setEditMode,
    clearFilterParams,
    setDartsGames,
    setGamesNoContent,
    setGameFilterValues,
    setActivePage,
    changePageSize
} = DartsGamesCRUD.actions;
export default DartsGamesCRUD.reducer;
